import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { Container, Card, Button, ErrorMessage } from '../styles/GlobalStyles';

const VerifyContainer = styled(Container)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: var(--spacing-md);
`;

const VerifyCard = styled(Card)`
  width: 100%;
  max-width: 500px;
  margin: 0;
  text-align: center;
`;

const Title = styled.h1`
  margin-bottom: var(--spacing-xl);
  color: var(--text-primary);
  font-size: 1.875rem;
  font-weight: 700;
`;

const Message = styled.div`
  margin-bottom: var(--spacing-xl);
  line-height: 1.6;
  color: var(--text-secondary);
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  color: #28a745;
  margin-bottom: var(--spacing-lg);
`;

const ErrorIcon = styled.div`
  font-size: 4rem;
  color: #dc3545;
  margin-bottom: var(--spacing-lg);
`;

const LoadingSpinner = styled.div`
  font-size: 3rem;
  margin-bottom: var(--spacing-lg);
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  margin-top: var(--spacing-xl);
`;

const StyledLink = styled(Link)`
  color: var(--primary-color);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

export const VerifyRegistrationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    const verifyRegistration = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. The verification token is missing.');
        return;
      }

      try {
        const { authService } = await import('../services/authService');
        await authService.verifyRegistration(token);
        setStatus('success');
        setMessage('Your email has been verified successfully! You can now log in to your account.');
      } catch (error: any) {
        setStatus('error');
        const errorMessage = error.response?.data?.error?.message || 'Verification failed.';
        
        if (errorMessage.includes('expired')) {
          setMessage('This verification link has expired. Please request a new verification email.');
        } else if (errorMessage.includes('invalid') || errorMessage.includes('already used')) {
          setMessage('This verification link is invalid or has already been used.');
        } else {
          setMessage(errorMessage);
        }
      }
    };

    verifyRegistration();
  }, [searchParams]);

  const handleResendVerification = async () => {
    const email = prompt('Please enter your email address to resend the verification email:');
    
    if (!email) {
      return;
    }

    setIsResending(true);
    
    try {
      const { authService } = await import('../services/authService');
      await authService.resendRegistrationVerification(email);
      alert('Verification email resent! Please check your inbox.');
    } catch (error: any) {
      alert(error.response?.data?.error?.message || 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  if (status === 'loading') {
    return (
      <VerifyContainer>
        <VerifyCard>
          <LoadingSpinner>⏳</LoadingSpinner>
          <Title>Verifying Your Email</Title>
          <Message>Please wait while we verify your email address...</Message>
        </VerifyCard>
      </VerifyContainer>
    );
  }

  if (status === 'success') {
    return (
      <VerifyContainer>
        <VerifyCard>
          <SuccessIcon>✓</SuccessIcon>
          <Title>Email Verified!</Title>
          <Message>{message}</Message>
          <ButtonGroup>
            <Button onClick={handleGoToLogin}>
              Go to Login
            </Button>
          </ButtonGroup>
        </VerifyCard>
      </VerifyContainer>
    );
  }

  return (
    <VerifyContainer>
      <VerifyCard>
        <ErrorIcon>✗</ErrorIcon>
        <Title>Verification Failed</Title>
        <ErrorMessage>{message}</ErrorMessage>
        <ButtonGroup>
          <Button onClick={handleResendVerification} disabled={isResending}>
            {isResending ? 'Resending...' : 'Resend Verification Email'}
          </Button>
          <StyledLink to="/login">Back to Login</StyledLink>
        </ButtonGroup>
      </VerifyCard>
    </VerifyContainer>
  );
};
